import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/client/components/common/Select";
import { Slider } from "@/client/components/common/Slider";
import { FeatureValue } from ".";

type FeatureField = {
  name: string;
  type: "enum" | "number" | "boolean";
  options?: string[];
  max?: number;
  min?: number;
  step?: number;
};

interface FeatureFieldProps {
  field: FeatureField;
  value: string | number | null;
  onChange: (value: string | number | null) => void;
  valueCounts?: Record<string, number>;
  totalCount?: number;
}

// Component for number-type features
const NumberFeatureField = ({
  field,
  value,
  onChange,
  valueCounts,
  totalCount,
}: FeatureFieldProps) => {
  const [inputValue, setInputValue] = useState(value?.toString() ?? "");
  const min = field.min ?? 0;
  const max = field.max ?? 100;

  const isValidInput = (input: string) => {
    const num = Number(input);
    return !isNaN(num) && num >= min && num <= max;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (isValidInput(e.target.value)) {
      onChange(Number(e.target.value));
    }
  };

  const handleInputBlur = () => {
    const num = Number(inputValue);
    if (isNaN(num)) {
      setInputValue(value?.toString() ?? "");
      return;
    }

    const clampedValue = Math.min(Math.max(num, min), max);
    setInputValue(clampedValue.toString());
    onChange(clampedValue);
  };

  const valueCount = valueCounts?.[value?.toString() ?? ""] ?? 0;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-2">
        <Slider
          value={[Number(value) || min]}
          min={min}
          max={max}
          step={field.step}
          onValueChange={([newValue]) => {
            setInputValue(newValue.toString());
            onChange(newValue);
          }}
        />
        <div className="flex gap-2">
          <input
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            min={min}
            max={max}
            step={field.step}
            className="bg-transparent text-foreground placeholder:text-foreground/50"
          />
          <button
            className="text-xs text-foreground disabled:text-foreground/20"
            onClick={() => {
              onChange(null);
              setInputValue("");
            }}
            disabled={!value}
          >
            Clear
          </button>
        </div>
      </div>
      {valueCount !== undefined && value !== null && totalCount ? (
        <p className="text-xs text-foreground-muted">
          ({valueCount} • {((valueCount / totalCount) * 100).toFixed(1)}%)
        </p>
      ) : null}
    </div>
  );
};

// Component for enum-type features
const EnumFeatureField = ({
  field,
  value,
  onChange,
  valueCounts,
  totalCount,
}: FeatureFieldProps) => {
  const massagedField =
    field.type === "boolean"
      ? {
          ...field,
          options: ["true", "false"],
          type: "enum",
        }
      : field;

  return (
    <div className="flex gap-2">
      <Select
        value={value?.toString() ?? ""}
        onValueChange={(newValue) => onChange(newValue || null)}
      >
        <SelectTrigger>
          <SelectValue placeholder={`Select ${massagedField.name}`} />
        </SelectTrigger>
        <SelectContent>
          {massagedField.options?.map((option) => {
            const valueCount = valueCounts?.[option];
            return (
              <SelectItem key={option} value={option}>
                <span>{option} </span>
                {valueCount && totalCount ? (
                  <span className="text-xs text-foreground-muted">
                    ({valueCount} •{" "}
                    {((valueCount / totalCount) * 100).toFixed(1)}%)
                  </span>
                ) : null}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      <button
        className="text-xs text-foreground disabled:text-foreground/20"
        onClick={() => onChange(null)}
        disabled={!value}
      >
        Clear
      </button>
    </div>
  );
};

interface FeatureSelectorProps {
  featureFields?: FeatureField[];
  featreFieldsCounts?: Record<string, Record<string, number>>;
  highlightedFeatures: FeatureValue[];
  filteredTokensCount?: number;
  onFeatureChange: (features: FeatureValue[]) => void;
  totalTokensCount?: number;
}

export function FeatureSelector({
  featureFields,
  highlightedFeatures,
  onFeatureChange,
  featreFieldsCounts,
  filteredTokensCount,
  totalTokensCount,
}: FeatureSelectorProps) {
  if (!featureFields) return null;

  const handleFeatureChange = (
    fieldName: string,
    value: string | number | null
  ) => {
    if (value === null) {
      onFeatureChange(
        highlightedFeatures.filter((f) => f.feature !== fieldName)
      );
    } else {
      const newFeatures = highlightedFeatures.filter(
        (f) => f.feature !== fieldName
      );
      newFeatures.push({ feature: fieldName, value });
      onFeatureChange(newFeatures);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="flex items-center gap-2 text-sm text-foreground-muted">
          <span>
            {!highlightedFeatures.length
              ? "No filters applied"
              : `${highlightedFeatures.length} filter${
                  highlightedFeatures.length > 1 ? "s" : ""
                } applied`}
          </span>
          <span className="text-xs text-foreground-muted/50">
            ({filteredTokensCount}/{totalTokensCount} tokens)
          </span>
        </p>
        <button
          className="text-xs text-foreground disabled:text-foreground/20"
          onClick={() => onFeatureChange([])}
          disabled={!highlightedFeatures.length}
        >
          Clear all
        </button>
      </div>
      <div className="space-y-4">
        {featureFields.map((field) => {
          const currentValue =
            highlightedFeatures.find((f) => f.feature === field.name)?.value ??
            null;
          const counts = featreFieldsCounts?.[field.name];

          return (
            <div key={field.name} className="space-y-2">
              <label className="text-sm font-medium text-foreground-muted">
                {field.name}
              </label>

              {field.type === "number" ? (
                <NumberFeatureField
                  field={field}
                  value={currentValue?.toString() ?? null}
                  onChange={(value) => handleFeatureChange(field.name, value)}
                  valueCounts={counts}
                  totalCount={totalTokensCount}
                />
              ) : (
                <EnumFeatureField
                  field={field}
                  value={currentValue?.toString() ?? null}
                  onChange={(value) => handleFeatureChange(field.name, value)}
                  valueCounts={counts}
                  totalCount={totalTokensCount}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
